/** Lógica pura del borrador editable de Reparto Inteligente (sin React, testeable). */

export const MOTIVOS_EXCLUSION = ["Pendiente permiso", "Material no disponible", "Cita pendiente", "Aplazar", "Otro"];

export const haversineKm = (a, b) => {
  const R = 6371, toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat), dLon = toRad(b.lon - a.lon);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

/** Recorrido greedy (vecino más cercano) desde `inicio` → km estimados. */
export const kmGreedy = (paradas, inicio) => {
  const pts = paradas.filter((p) => p.lat != null && p.lon != null);
  if (!pts.length) return 0;
  let cur = inicio || pts[0], total = 0;
  const rest = inicio ? [...pts] : pts.slice(1);
  while (rest.length) {
    let bi = 0, bd = Infinity;
    rest.forEach((p, i) => { const d = haversineKm(cur, p); if (d < bd) { bd = d; bi = i; } });
    total += bd; cur = rest.splice(bi, 1)[0];
  }
  return Math.round(total * 10) / 10;
};

const modo = (arr) => {
  const c = new Map();
  arr.filter(Boolean).forEach((v) => c.set(v, (c.get(v) || 0) + 1));
  return [...c.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || null;
};

/** Recalcula los derivados de un grupo tras editar sus paradas. */
export const recomputeGrupo = (g, inicio) => {
  const paradas = g.paradas;
  const cap = g.capacidad ?? null;
  const horas = paradas.map((p) => p.horas_estimadas).filter((h) => typeof h === "number");
  const bloquesCnt = new Map();
  paradas.forEach((p) => bloquesCnt.set(p.bloque_key || p.espacio_id || p.linea_id, (bloquesCnt.get(p.bloque_key || p.espacio_id || p.linea_id) || 0) + 1));
  const bloques = [...bloquesCnt.entries()].filter(([, n]) => n > 1).map(([key, n]) => ({
    key, n, label: paradas.find((p) => (p.bloque_key || p.espacio_id || p.linea_id) === key)?.espacio_nombre || "destino",
  }));
  return {
    ...g, paradas,
    n_paradas: paradas.length,
    carga: cap ? `${paradas.length}/${cap}` : String(paradas.length),
    distancia_km_estimada: kmGreedy(paradas, inicio),
    horas_montaje_estimadas: horas.length ? Math.round(horas.reduce((a, b) => a + b, 0) * 10) / 10 : null,
    paradas_con_horas: horas.length,
    ciudad_principal: modo(paradas.map((p) => p.ciudad)),
    campanas: [...new Set(paradas.map((p) => `${p.peticion_codigo || "—"}${p.marca ? ` · ${p.marca}` : ""}`))].sort(),
    bloques,
    n_destinos: bloquesCnt.size,
  };
};

const findParada = (grupos, lineaId) => {
  for (const g of grupos) { const p = g.paradas.find((x) => x.linea_id === lineaId); if (p) return { g, p }; }
  return null;
};

/** Mueve una parada a otro grupo. Devuelve {grupos, error}. Respeta capacidad dura. */
export const moverParada = (grupos, lineaId, destinoId, inicio) => {
  const src = findParada(grupos, lineaId);
  if (!src) return { grupos, error: "Parada no encontrada" };
  if (src.g.id === destinoId) return { grupos, error: null };
  const dst = grupos.find((g) => g.id === destinoId);
  if (!dst) return { grupos, error: "Ruta destino no encontrada" };
  if (dst.paradas.some((p) => p.linea_id === lineaId)) return { grupos, error: null };
  if (dst.capacidad && dst.paradas.length >= dst.capacidad) {
    return { grupos, error: `${dst.id} está al máximo (${dst.paradas.length}/${dst.capacidad}). No se puede superar el límite de paradas.` };
  }
  const nuevos = grupos.map((g) => {
    if (g.id === src.g.id) return recomputeGrupo({ ...g, paradas: g.paradas.filter((p) => p.linea_id !== lineaId) }, inicio);
    if (g.id === destinoId) return recomputeGrupo({ ...g, paradas: [...g.paradas, src.p] }, inicio);
    return g;
  });
  return { grupos: nuevos, error: null };
};

/** Excluye una parada de la propuesta (sin tocar la línea real). */
export const excluirParada = (grupos, excluidas, lineaId, motivo, inicio) => {
  const src = findParada(grupos, lineaId);
  if (!src) return { grupos, excluidas };
  return {
    grupos: grupos.map((g) => g.id === src.g.id ? recomputeGrupo({ ...g, paradas: g.paradas.filter((p) => p.linea_id !== lineaId) }, inicio) : g),
    excluidas: [...excluidas, { ...src.p, fijada: false, motivo: motivo || "Otro", grupo_origen: src.g.id }],
  };
};

/** Restaura una excluida a un grupo (por defecto el de origen si tiene hueco). */
export const restaurarParada = (grupos, excluidas, lineaId, destinoId, inicio) => {
  const p = excluidas.find((x) => x.linea_id === lineaId);
  if (!p) return { grupos, excluidas, error: "No encontrada" };
  const dst = grupos.find((g) => g.id === (destinoId || p.grupo_origen)) || grupos[0];
  if (!dst) return { grupos, excluidas, error: "No hay rutas" };
  if (grupos.some((g) => g.paradas.some((x) => x.linea_id === lineaId))) {
    return { grupos, excluidas: excluidas.filter((x) => x.linea_id !== lineaId), error: null }; // ya estaba en una ruta
  }
  if (dst.capacidad && dst.paradas.length >= dst.capacidad) return { grupos, excluidas, error: `${dst.id} está al máximo (${dst.carga})` };
  const { motivo, grupo_origen, ...limpia } = p;
  return {
    grupos: grupos.map((g) => g.id === dst.id ? recomputeGrupo({ ...g, paradas: [...g.paradas, limpia] }, inicio) : g),
    excluidas: excluidas.filter((x) => x.linea_id !== lineaId), error: null,
  };
};

export const toggleFijar = (grupos, lineaId) =>
  grupos.map((g) => ({ ...g, paradas: g.paradas.map((p) => p.linea_id === lineaId ? { ...p, fijada: !p.fijada } : p) }));

/** Elimina grupos vacíos (renumerando ids no es necesario: se conservan). */
export const limpiarVacios = (grupos) => grupos.filter((g) => g.paradas.length > 0);

/** Añade una ruta vacía nueva (para mover paradas cuando todo está lleno). */
export const anadirGrupoVacio = (grupos, capacidad) => {
  const n = grupos.length + 1;
  return [...grupos, recomputeGrupo({ id: `G${n}`, capacidad, paradas: [], advertencias: [], montador: null }, null)];
};

/** Resumen para el diálogo "Crear todas". */
export const resumenCreacion = (grupos, excluidas, fechaPlan) => ({
  fecha: fechaPlan,
  rutas: grupos.filter((g) => g.paradas.length).map((g) => ({
    id: g.id, n: g.paradas.length, zona: g.ciudad_principal, montador: g.montador?.nombre_visible || null,
  })),
  excluidas: excluidas.length,
  totalParadas: grupos.reduce((a, g) => a + g.paradas.length, 0),
});

/** Orden greedy (vecino más cercano) de las paradas de una ruta desde `inicio`. Las sin geo van al final. */
export const ordenarGreedy = (paradas, inicio) => {
  const geo = paradas.filter((p) => p.lat != null && p.lon != null);
  const sinGeo = paradas.filter((p) => p.lat == null || p.lon == null);
  if (geo.length < 2) return [...geo, ...sinGeo];
  const rest = [...geo], out = [];
  let cur = inicio;
  if (!cur) { out.push(rest.shift()); cur = out[0]; }
  while (rest.length) {
    let bi = 0, bd = Infinity;
    rest.forEach((p, i) => { const d = haversineKm(cur, p); if (d < bd) { bd = d; bi = i; } });
    cur = rest.splice(bi, 1)[0]; out.push(cur);
  }
  return [...out, ...sinGeo];
};

/** Añade paradas (oportunidades) a una ruta del borrador respetando la capacidad dura; reordena (greedy). */
export const anadirParadas = (grupos, grupoId, nuevas, inicio) => {
  const dst = grupos.find((g) => g.id === grupoId);
  if (!dst) return { grupos, error: "Ruta no encontrada" };
  const enBorrador = new Set(grupos.flatMap((g) => g.paradas.map((p) => p.linea_id)));
  const limpias = dedupeLineaIds(nuevas.map((p) => p.linea_id)).filter((id) => !enBorrador.has(id)).map((id) => nuevas.find((p) => p.linea_id === id));
  if (!limpias.length) return { grupos, error: "Esas paradas ya están en el borrador" };
  if (dst.capacidad && dst.paradas.length + limpias.length > dst.capacidad) {
    return { grupos, error: `${dst.id} quedaría en ${dst.paradas.length + limpias.length}/${dst.capacidad}: supera el máximo de paradas. Súbelo o usa otra ruta.` };
  }
  const paradas = ordenarGreedy([...dst.paradas, ...limpias.map((p) => ({ ...p, fijada: false, oportunidad: true }))], inicio);
  return { grupos: grupos.map((g) => g.id === grupoId ? recomputeGrupo({ ...g, paradas }, inicio) : g), error: null, anadidas: limpias.length };
};

/** Dedupe estable de linea_ids preservando el orden. */
export const dedupeLineaIds = (ids) => {
  const seen = new Set();
  return (ids || []).filter((id) => id && !seen.has(id) && seen.add(id));
};

/** Sanea una propuesta: una parada por linea_id en TODO el borrador (se queda la primera aparición, por orden de grupo). */
export const sanearGrupos = (grupos, inicio) => {
  const seen = new Set();
  let repetidas = 0;
  const limpios = grupos.map((g) => {
    const paradas = g.paradas.filter((p) => {
      if (!p.linea_id || seen.has(p.linea_id)) { repetidas++; return false; }
      seen.add(p.linea_id); return true;
    });
    return paradas.length === g.paradas.length ? g : recomputeGrupo({ ...g, paradas }, inicio);
  });
  return { grupos: limpios, repetidas };
};
